import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { PhCaretUp, PhCaretDown } from 'ember-phosphor-icons';
import type Owner from '@ember/owner';

interface ExpandableSectionSignature {
  Args: {
    title: string;
    defaultExpanded?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

/**
 * Expandable Section Component
 * Provides a collapsible section with a header and content area
 * Used in drawers to group related content that can be shown/hidden
 */
class ExpandableSection extends Component<ExpandableSectionSignature> {
  @tracked isExpanded: boolean;

  constructor(owner: Owner, args: ExpandableSectionSignature['Args']) {
    super(owner, args);
    this.isExpanded = args.defaultExpanded ?? true;
  }

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  <template>
    <div class="tio-col-loose w-full" ...attributes>
      {{! Section Header }}
      <button
        type="button"
        class="tio-row-between w-full cursor-pointer"
        {{on "click" this.toggleExpanded}}
        aria-expanded={{if this.isExpanded "true" "false"}}
      >
        <span class="tio-text-heading tio-fg-secondary leading-relaxed">
          {{@title}}
        </span>
        {{#if this.isExpanded}}
          <PhCaretUp @size={{24}} class="tio-fg-muted shrink-0" />
        {{else}}
          <PhCaretDown @size={{24}} class="tio-fg-muted shrink-0" />
        {{/if}}
      </button>

      {{! Section Content }}
      {{#if this.isExpanded}}
        <div class="pr-10">
          {{yield}}
        </div>
      {{/if}}
    </div>
  </template>
}

export default ExpandableSection;
